import { getGraphAccessToken } from './msal'

// ---------------------------------------------------------------------------
// Microsoft Graph API for the signed-in user's own profile (e.g. avatar).
// Calls Microsoft Graph directly from the browser (no proxy needed -- Graph
// supports CORS for SPA requests) using the MSAL-acquired Graph access token.
// ---------------------------------------------------------------------------

const GRAPH_PHOTO_URL = 'https://graph.microsoft.com/v1.0/me/photo/$value'

/**
 * Fetches the signed-in user's Microsoft 365 profile photo and returns an
 * object URL for it. Returns `null` when the user has no photo set, no MSAL
 * account is active, or the request otherwise fails -- callers should fall
 * back to a generic avatar in that case.
 *
 * The caller is responsible for revoking the returned object URL (e.g. on
 * unmount) via `URL.revokeObjectURL` to avoid leaking memory.
 */
export async function getProfilePhotoUrl(): Promise<string | null> {
  const token = await getGraphAccessToken()
  if (!token) return null

  let response: Response
  try {
    response = await fetch(GRAPH_PHOTO_URL, {
      headers: { Authorization: `Bearer ${token}` },
    })
  } catch {
    return null
  }

  if (!response.ok) {
    // 404 means the user simply has no photo set -- not an error.
    return null
  }

  const blob = await response.blob()
  return URL.createObjectURL(blob)
}
